import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  BookOpen,
  Briefcase,
  ChevronRight,
  CircleCheck,
  FileText,
  HeartHandshake,
  House,
  Megaphone,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  PenLine,
  Plug,
  Radar,
  Settings,
  ShieldCheck,
  Users,
  Wallet,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type { FeatureId } from '@sequesto-os/shared';
import { useSidebarContext } from '../../context/SidebarContext';
import { useIntegrationExceptions } from '../SystemDiagram';
import { useAccess } from '../../hooks/useAccess';
import { useTaskCounts } from '../../hooks/useOrganizer';
import { useQuery } from '@tanstack/react-query';
import { getSignatures } from '../../api/signatures';

interface NavItem {
  name: string;
  feature: FeatureId;
  href: string;
  icon: LucideIcon;
  /**
   * Renders a live count. Resolved per item at render time so only the one link
   * that needs data subscribes to a query.
   */
  liveBadge?: 'organizer';
  /** Left out of the sidebar while it has nothing in it; the route stays. */
  hideWhenEmpty?: 'signatures';
}

interface NavGroup {
  label: string | null;
  items: NavItem[];
  /** Folded behind its label until clicked; opens by itself on one of its pages. */
  collapsible?: boolean;
}

// Admin pages also stay in the avatar menu (Header). Every item is filtered by
// canAccessArea(feature), the same guard the avatar menu and the routes use.
const navGroups: NavGroup[] = [
  {
    label: null,
    items: [{ name: 'Home', feature: 'home', href: '/', icon: House }],
  },
  {
    label: 'Departments',
    items: [
      { name: 'Sales', feature: 'sales', href: '/sales', icon: Briefcase },
      { name: 'CSM', feature: 'csm', href: '/csm', icon: HeartHandshake },
      { name: 'GTM', feature: 'gtm', href: '/gtm', icon: Radar },
      { name: 'Marketing', feature: 'marketing', href: '/marketing', icon: Megaphone },
      { name: 'Finance', feature: 'finance', href: '/finance', icon: Wallet },
    ],
  },
  {
    label: 'Company',
    items: [
      { name: 'Resources', feature: 'resources', href: '/resources', icon: BookOpen },
      { name: 'Brand docs', feature: 'documents', href: '/documents', icon: FileText },
    ],
  },
  {
    label: 'Personal',
    items: [{ name: 'Organizer', feature: 'organizer', href: '/organizer', icon: CircleCheck, liveBadge: 'organizer' }],
  },
  {
    label: 'Admin',
    collapsible: true,
    items: [
      { name: 'Settings', feature: 'settings', href: '/settings', icon: Settings },
      { name: 'Users', feature: 'users', href: '/users', icon: Users },
      { name: 'Automations', feature: 'automations', href: '/automations', icon: Zap },
      { name: 'Integrations', feature: 'integrations', href: '/integrations', icon: Plug },
      { name: 'Signatures', feature: 'signatures', href: '/signatures', icon: PenLine, hideWhenEmpty: 'signatures' },
    ],
  },
];

const SIGNATURES_EMPTY_KEY = 'os.sidebar.signaturesEmpty';

function readStoredEmpty(): boolean | null {
  try {
    const v = localStorage.getItem(SIGNATURES_EMPTY_KEY);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}

/**
 * Whether the Signatures link has anything behind it. Same query key as the
 * Signatures page. Until the API answers, the last answer this browser saw
 * decides (remembered), so the link does not appear and then vanish on every
 * load; with no answer yet at all it stays hidden. On an error it shows, so a
 * failed read never hides a page.
 */
function useSignaturesEmpty(enabled: boolean): boolean {
  const { data, error } = useQuery({ queryKey: ['signatures'], queryFn: getSignatures, enabled, staleTime: 5 * 60_000 });
  const known = data === undefined ? null : data.signatures.length === 0;
  useEffect(() => {
    if (known === null) return;
    try {
      localStorage.setItem(SIGNATURES_EMPTY_KEY, known ? '1' : '0');
    } catch {
      // Remembering is a convenience; without it the link just waits for the API.
    }
  }, [known]);
  if (error) return false;
  return known ?? readStoredEmpty() ?? true;
}

type IssueStatus = 'loading' | 'ok' | 'error' | 'warning' | 'unavailable';
type Issues = { status: IssueStatus; message: string };

/**
 * The last value a sidebar signal showed, kept per browser so a page load
 * shows it at once instead of a "Checking…" flash; replaced as soon as fresh
 * data lands. Storage can be missing or blocked: every access is guarded.
 */
function readCached<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function writeCached(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Per-viewer convenience only; the live value is on screen either way.
  }
}

const ISSUES_CACHE_KEY = 'sidebar.integrationIssues';
const ORGANIZER_CACHE_KEY = 'sidebar.organizerDue';

/**
 * Integrations with a problem, from the same derived status the Integrations
 * page and Home use. Names the integration when only one is failing. While
 * loading: the last value shown (from this browser), else nothing — the
 * footer's height is reserved either way.
 */
function useIntegrationIssues(enabled: boolean): Issues {
  const { exceptions, isLoading, error } = useIntegrationExceptions(enabled);
  const [cached] = useState(() => readCached<Issues>(ISSUES_CACHE_KEY));

  const issues = useMemo<Issues>(() => {
    if (isLoading) return cached ?? { status: 'loading', message: '' };
    if (error) return { status: 'unavailable', message: 'Integration status unavailable' };
    const failing = exceptions.filter((e) => e.health.state === 'failing');
    const stuck = exceptions.filter((e) => e.health.state === 'stuck');
    if (failing.length === 1 && stuck.length === 0) return { status: 'error', message: `${failing[0].name} failing` };
    if (failing.length > 0) {
      return {
        status: 'error',
        message: `${failing.length} integrations failing${stuck.length > 0 ? ` · ${stuck.length} stuck` : ''}`,
      };
    }
    if (stuck.length > 0) {
      return { status: 'warning', message: stuck.length === 1 ? `${stuck[0].name} stuck` : `${stuck.length} integrations stuck` };
    }
    return { status: 'ok', message: 'All integrations running' };
  }, [exceptions, isLoading, error, cached]);

  useEffect(() => {
    if (!isLoading && !error) writeCached(ISSUES_CACHE_KEY, issues);
  }, [issues, isLoading, error]);
  return issues;
}

/**
 * Overdue + due-today count on the Organizer link.
 *
 * The Sidebar renders once for every private route, so this is the app's only
 * poller for the count; the Organizer page reads the same query key and gets the
 * cache entry for free, which is what keeps the badge and the page in agreement.
 * An error hides the badge — the Organizer page itself surfaces it.
 */
function OrganizerBadge({ collapsed }: { collapsed: boolean }) {
  const { canAccessArea } = useAccess();
  const { data, isError, isLoading } = useTaskCounts(canAccessArea('organizer'));
  const [cached] = useState(() => readCached<number>(ORGANIZER_CACHE_KEY));
  const live = data ? data.counts.overdue + data.counts.dueToday : null;
  useEffect(() => {
    if (live !== null) writeCached(ORGANIZER_CACHE_KEY, live);
  }, [live]);
  if (isError) return null;

  // While loading: the last count this browser showed, else nothing (no placeholder flash).
  const count = isLoading ? (typeof cached === 'number' ? cached : 0) : (live ?? 0);
  if (!count) return null;

  if (collapsed) {
    return <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-error" aria-label={`${count} due`} />;
  }
  return (
    <span className="ml-auto rounded-full bg-error-light px-2 py-0.5 text-body-xs font-semibold leading-none text-error-dark">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.href}
      end={item.href === '/'}
      title={collapsed ? item.name : undefined}
      aria-label={collapsed ? item.name : undefined}
      className={({ isActive }) =>
        clsx(
          'group relative flex items-center rounded-lg text-body-sm font-medium transition-colors duration-100',
          collapsed ? 'h-9 w-9 justify-center' : 'gap-2.5 px-2.5 py-1.5',
          isActive
            ? 'bg-surface-sunken text-text-primary'
            : 'text-text-secondary hover:bg-surface-tertiary hover:text-text-primary'
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={clsx(
              'h-4 w-4 flex-none',
              isActive ? 'text-text-primary' : 'text-text-tertiary group-hover:text-text-primary'
            )}
            aria-hidden
          />
          {!collapsed && item.name}
          {item.liveBadge === 'organizer' && <OrganizerBadge collapsed={collapsed} />}
        </>
      )}
    </NavLink>
  );
}

const ADMIN_OPEN_KEY = 'sidebar.adminOpen';

/** Remembered per browser; storage can be blocked, so every access is guarded. */
function useStoredToggle(key: string): [boolean, () => void] {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(key, next ? '1' : '0');
      } catch {
        // Not remembered across reloads; the toggle still works.
      }
      return next;
    });
  return [open, toggle];
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isCollapsed: collapsed, collapseSidebar, expandSidebar } = useSidebarContext();
  const { canAccessArea } = useAccess();
  const canSeeIntegrations = canAccessArea('integrations');
  const issues = useIntegrationIssues(canSeeIntegrations);

  const signaturesEmpty = useSignaturesEmpty(canAccessArea('signatures'));
  const isHidden = (item: NavItem) =>
    item.hideWhenEmpty === 'signatures' && signaturesEmpty && !location.pathname.startsWith(item.href);

  const visibleGroups = navGroups
    .map((group) => ({ ...group, items: group.items.filter((item) => canAccessArea(item.feature) && !isHidden(item)) }))
    .filter((group) => group.items.length > 0);

  const [adminOpen, toggleAdmin] = useStoredToggle(ADMIN_OPEN_KEY);

  const canAccessJames = canAccessArea('james');
  const onJames = location.pathname.startsWith('/james');

  return (
    <aside
      className={clsx(
        'flex h-full flex-col border-r border-border-light bg-surface-secondary',
        collapsed ? 'w-14 items-center' : 'w-64'
      )}
    >
      {/* Logo + collapse toggle. Collapsed, the toggle stays in this same top row:
          the logo mark turns into the expand icon on hover, so nothing jumps. */}
      {collapsed ? (
        <div className="flex h-14 flex-none items-center justify-center">
          <button
            type="button"
            onClick={expandSidebar}
            title="Expand sidebar"
            aria-label="Expand sidebar"
            className="group/expand relative flex h-8 w-8 items-center justify-center rounded-lg text-text-tertiary transition-colors hover:bg-surface-tertiary hover:text-text-primary"
          >
            <img
              src="/sequesto-icon.png"
              alt=""
              className="h-6 w-6 rounded-md select-none group-hover/expand:opacity-0 group-focus-visible/expand:opacity-0"
              draggable={false}
            />
            <PanelLeftOpen className="absolute h-4 w-4 opacity-0 group-hover/expand:opacity-100 group-focus-visible/expand:opacity-100" />
          </button>
        </div>
      ) : (
        <div className="flex h-14 flex-none items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2 px-1.5">
            <img src="/sequesto-logo-dark.png" alt="SEQUESTO" className="h-3.5 w-auto select-none" draggable={false} />
            <span className="eyebrow">OS</span>
          </Link>
          <button
            type="button"
            onClick={collapseSidebar}
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-text-tertiary transition-colors hover:bg-surface-tertiary hover:text-text-primary"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Ask James — the one James entry in the chrome */}
      {canAccessJames && (
        <div className={clsx('pb-2', collapsed ? 'px-2.5' : 'px-3')}>
          <button
            type="button"
            onClick={() => navigate('/james')}
            title="Open James · ⌘K for the side panel"
            aria-label="Ask James"
            className={clsx(
              'flex items-center rounded-lg border text-body-sm font-medium shadow-subtle transition-colors',
              collapsed ? 'h-9 w-9 justify-center' : 'w-full gap-2.5 px-2.5 py-1.5',
              onJames
                ? 'border-border bg-surface-tertiary text-text-primary'
                : 'border-border-light bg-surface-primary text-text-secondary hover:border-border hover:text-text-primary'
            )}
          >
            <MessageSquare className="h-4 w-4 flex-none text-text-tertiary" aria-hidden />
            {!collapsed && (
              <>
                Ask James
                <kbd className="ml-auto font-sans text-body-xs text-text-muted">⌘K</kbd>
              </>
            )}
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className={clsx('flex-1 overflow-y-auto py-2', collapsed ? 'px-2.5' : 'px-3')}>
        {visibleGroups.map((group, i) => (
          <div
            key={group.label ?? i}
            className={clsx(i > 0 && (collapsed ? 'mt-3 border-t border-border-light pt-3' : 'mt-5'))}
          >
            {(() => {
              const onGroupPage = group.items.some((item) => location.pathname.startsWith(item.href));
              const open = !group.collapsible || adminOpen || onGroupPage;
              return (
                <>
                  {group.collapsible ? (
                    <button
                      type="button"
                      onClick={toggleAdmin}
                      aria-expanded={open}
                      title={collapsed ? group.label ?? undefined : undefined}
                      aria-label={collapsed ? group.label ?? undefined : undefined}
                      className={clsx(
                        'group flex items-center rounded-lg text-text-muted transition-colors hover:bg-surface-tertiary hover:text-text-primary',
                        collapsed ? 'h-9 w-9 justify-center' : 'mb-1 w-full gap-1.5 px-2.5 py-1'
                      )}
                    >
                      {collapsed ? (
                        <ShieldCheck className="h-4 w-4 flex-none text-text-tertiary group-hover:text-text-primary" aria-hidden />
                      ) : (
                        <>
                          <span className="eyebrow text-inherit">{group.label}</span>
                          <ChevronRight
                            className={clsx('h-3.5 w-3.5 transition-transform', open && 'rotate-90')}
                            aria-hidden
                          />
                        </>
                      )}
                    </button>
                  ) : (
                    group.label && !collapsed && <p className="eyebrow px-2.5 pb-2 text-text-muted">{group.label}</p>
                  )}
                  {open && (
                    <div className="space-y-0.5">
                      {group.items.map((item) => (
                        <SidebarLink key={item.name} item={item} collapsed={collapsed} />
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ))}
      </nav>

      {/* System signal for those who can reach Integrations. Always rendered so
          its height is reserved from first paint; quiet when all is well. */}
      {canSeeIntegrations && (
        <Link
          to="/integrations"
          title={issues.message || undefined}
          className={clsx(
            'flex h-10 flex-none items-center gap-2 border-t border-border-light text-body-xs hover:text-text-primary',
            issues.status === 'ok' || issues.status === 'loading' ? 'text-text-muted' : 'text-text-secondary',
            collapsed ? 'w-full justify-center' : 'px-5'
          )}
        >
          <span
            className={clsx(
              'h-2 w-2 flex-none rounded-full',
              issues.status === 'error'
                ? 'bg-error'
                : issues.status === 'warning' || issues.status === 'unavailable'
                  ? 'bg-warning'
                  : issues.status === 'ok'
                    ? 'bg-success/60'
                    : 'invisible'
            )}
            aria-hidden
          />
          {!collapsed && <span className="truncate">{issues.message}</span>}
        </Link>
      )}
    </aside>
  );
}
